import { type CalculatorDef, type CalcResult, checkFinite, err, num, ok } from './types';

export type BlendResult = {
  /** Strength of the combined spirit, %ABV. */
  abv: number;
  /** Litres after combining. */
  totalVolume: number;
  /** Litres of pure ethanol in the blend. */
  absoluteAlcohol: number;
}

/**
 * Combine two spirits of different strengths — the usual case being a strong heart cut
 * softened with a weaker run, or two barrels married before bottling.
 *
 * Ethanol is conserved and volumes are assumed to add, so the blend strength is the
 * volume-weighted mean:
 *
 *     abv = (v1 x a1 + v2 x a2) / (v1 + v2)
 *
 * Same ideal-mixing caveat as {@link dilute}: real volumes contract slightly, so the true
 * strength runs a touch higher than this predicts.
 */
export function blend(
  volumeA: number,
  abvA: number,
  volumeB: number,
  abvB: number,
): CalcResult<BlendResult> {
  const invalid = checkFinite({ volumeA, abvA, volumeB, abvB });
  if (invalid) return { ok: false, error: invalid };

  if (volumeA < 0) return err('error.volumeNonNegative', undefined, 'volumeA');
  if (volumeB < 0) return err('error.volumeNonNegative', undefined, 'volumeB');
  if (volumeA + volumeB <= 0) return err('error.volumePositive', undefined, 'volumeA');
  if (abvA < 0 || abvA > 100) return err('error.abvRange', undefined, 'abvA');
  if (abvB < 0 || abvB > 100) return err('error.abvRange', undefined, 'abvB');

  const totalVolume = volumeA + volumeB;
  const absoluteAlcohol = (volumeA * abvA + volumeB * abvB) / 100;

  return ok({
    abv: (absoluteAlcohol / totalVolume) * 100,
    totalVolume,
    absoluteAlcohol,
  });
}

export const blendCalculator: CalculatorDef = {
  id: 'blend',
  notesKey: 'notes.idealMixing',
  fields: [
    { name: 'volumeA', unit: 'litre', min: 0, max: 10000, step: 0.1, defaultValue: 5 },
    { name: 'abvA', unit: 'percent', min: 0, max: 100, step: 0.1, defaultValue: 65 },
    { name: 'volumeB', unit: 'litre', min: 0, max: 10000, step: 0.1, defaultValue: 5 },
    { name: 'abvB', unit: 'percent', min: 0, max: 100, step: 0.1, defaultValue: 40 },
  ],
  outputs: [
    { name: 'abv', unit: 'percent', precision: 2, primary: true },
    { name: 'totalVolume', unit: 'litre', precision: 2 },
    { name: 'absoluteAlcohol', unit: 'litre', precision: 2 },
  ],
  compute: (i) => blend(num(i, 'volumeA'), num(i, 'abvA'), num(i, 'volumeB'), num(i, 'abvB')),
};
